import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Save, X, Edit2, Trash2, Briefcase } from "lucide-react";
import { toast } from "sonner";

export default function FunktionerTab({ token, eventId, onCountChange, showConfirm, closeConfirm }) {
  const [mainRoles, setMainRoles] = useState([]);
  const [showAddMainRoleForm, setShowAddMainRoleForm] = useState(false);
  const [editingMainRole, setEditingMainRole] = useState(null);
  const [newMainRole, setNewMainRole] = useState({ name: "", description: "", has_areas: false, has_work_zones: false, shift_ids: [] });

  const loadMainRoles = async () => {
    try {
      const res = await axios.get(`${API}/events/${eventId}/main-roles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = res.data || [];
      setMainRoles(data);
      if (onCountChange) onCountChange(data.length);
    } catch (error) {
      console.error("Fejl ved hentning af funktioner", error);
    }
  };

  useEffect(() => { loadMainRoles(); }, [eventId]);

  const saveMainRole = async () => {
    if (!newMainRole.name.trim()) { toast.error("Indtast et navn"); return; }
    try {
      if (editingMainRole) {
        await axios.put(`${API}/events/${eventId}/main-roles/${editingMainRole.id}`, newMainRole, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Funktion opdateret!");
      } else {
        await axios.post(`${API}/events/${eventId}/main-roles`, newMainRole, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Funktion oprettet!");
      }
      setShowAddMainRoleForm(false);
      setEditingMainRole(null);
      setNewMainRole({ name: "", description: "", has_areas: false, has_work_zones: false, shift_ids: [] });
      loadMainRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fejl ved gemning");
    }
  };

  const deleteMainRole = (mainRole) => {
    showConfirm(
      "Slet funktion?",
      `Er du sikker på du vil slette "${mainRole.name}"? Tilknyttede teams vil miste deres funktion.`,
      async () => {
        try {
          await axios.delete(`${API}/events/${eventId}/main-roles/${mainRole.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          toast.success("Funktion slettet");
          loadMainRoles();
        } catch (error) {
          toast.error("Fejl ved sletning af funktion");
        }
        closeConfirm();
      }
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">Funktioner</h2>
          <p className="text-sm text-muted-foreground">Overordnede funktioner (Sweeper, Chauffør, Guide...)</p>
        </div>
        <Button onClick={() => { setEditingMainRole(null); setNewMainRole({ name: "", description: "", has_areas: false, has_work_zones: false, shift_ids: [] }); setShowAddMainRoleForm(true); }}>
          <Plus className="h-4 w-4 mr-2" />Tilføj funktion
        </Button>
      </div>

      {showAddMainRoleForm && (
        <div className="p-6 bg-card border border-border rounded-lg">
          <h3 className="font-bold text-lg mb-4">{editingMainRole ? 'Rediger funktion' : 'Tilføj ny funktion'}</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>Navn *</Label><Input placeholder="F.eks. Sweeper, Chauffør, Guide" value={newMainRole.name} onChange={(e) => setNewMainRole({...newMainRole, name: e.target.value})} /></div>
              <div><Label>Beskrivelse</Label><Input placeholder="Kort beskrivelse af rollen" value={newMainRole.description} onChange={(e) => setNewMainRole({...newMainRole, description: e.target.value})} /></div>
            </div>
            <div className="p-4 bg-muted/30 rounded-lg space-y-3">
              <p className="text-sm font-medium mb-2">Denne funktion har:</p>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={newMainRole.has_areas} onChange={(e) => setNewMainRole({...newMainRole, has_areas: e.target.checked})} className="w-5 h-5 rounded border-border" />
                <div><span className="font-medium">Flere områder</span><p className="text-sm text-muted-foreground">F.eks. Sweeper har Meadow, Camping, Backstage...</p></div>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={newMainRole.has_work_zones} onChange={(e) => setNewMainRole({...newMainRole, has_work_zones: e.target.checked})} className="w-5 h-5 rounded border-border" />
                <div><span className="font-medium">Flere arbejdszoner</span><p className="text-sm text-muted-foreground">F.eks. Sweep1, Sweep2, Sweep3 inden for hvert område</p></div>
              </label>
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <Button onClick={saveMainRole}><Save className="h-4 w-4 mr-2" />{editingMainRole ? 'Gem ændringer' : 'Opret funktion'}</Button>
            <Button variant="outline" onClick={() => { setShowAddMainRoleForm(false); setEditingMainRole(null); }}><X className="h-4 w-4 mr-2" />Annuller</Button>
          </div>
        </div>
      )}

      {mainRoles.length === 0 ? (
        <div className="text-center py-12 bg-card/30 rounded-lg border border-dashed border-border">
          <Briefcase className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">Ingen funktioner oprettet</h3>
          <p className="text-muted-foreground mb-4">Opret funktioner som Sweeper, Chauffør, Guide...</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {mainRoles.map(mainRole => (
            <div key={mainRole.id} className="bg-card border border-border rounded-lg p-4 hover:border-primary/50 transition-colors">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-bold">{mainRole.name}</h3>
                    <span className="px-2 py-0.5 bg-muted text-muted-foreground rounded text-xs">{mainRole.linked_roles_count || 0} tilknyttede teams</span>
                  </div>
                  {mainRole.description && <p className="text-sm text-muted-foreground mb-3">{mainRole.description}</p>}
                  <div className="flex gap-4 text-sm">
                    <span className={mainRole.has_areas ? "text-green-400" : "text-muted-foreground"}>{mainRole.has_areas ? "✓ Har flere områder" : "○ Ingen områder"}</span>
                    <span className={mainRole.has_work_zones ? "text-green-400" : "text-muted-foreground"}>{mainRole.has_work_zones ? "✓ Har flere arbejdszoner" : "○ Ingen arbejdszoner"}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={() => { setEditingMainRole(mainRole); setNewMainRole({ name: mainRole.name, description: mainRole.description || "", has_areas: mainRole.has_areas, has_work_zones: mainRole.has_work_zones, shift_ids: mainRole.shift_ids || [] }); setShowAddMainRoleForm(true); }}>
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" className="text-destructive hover:bg-destructive/10" onClick={() => deleteMainRole(mainRole)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
